/**
 * Sync Status Chip (Spoor 2, Verlof MVP — Story 3.3, UX V5)
 *
 * Deliberately DIFFERENT terminology from the besluit-status (In afwachting/
 * Goedgekeurd/Afgewezen, `StatusBadge.tsx`) — the UX spec is explicit that
 * these must not read as the same axis (AD-1: request_status en sync_status
 * zijn twee onafhankelijke assen, nooit vermengen):
 *   pending -> "nog te boeken" (neutraal)
 *   synced  -> "geboekt" (groen)
 *   failed  -> "mislukt" (rood) + Opnieuw proberen-knop
 *
 * The besluit blijft Goedgekeurd óók als de boeking mislukt — deze chip
 * staat bewust los van `StatusBadge`, niet erin verweven.
 *
 * VIERDE WAARDE, GEEN VIERDE CHIP (besluit B-V1, story 5.1). Verlof van een
 * soort die nooit in de loonadministratie belandt draagt `niet_boekend`. Daar
 * toont dit component NIETS — geen chip, geen vervangende zin. Er is voor die
 * toestand geen tekst vastgesteld, en er bestaat er ook geen: een regel als
 * "hier hoef je niets mee te doen" maakt pas een lege plek waar er geen was
 * (PRD-besluiten V27/V28). Een label verzinnen is verboden (UX-DR14).
 *
 * De twee tabellen hieronder dekken daarom bewust alleen de drie toestanden
 * die WÉL iets tonen. Ze zijn geen `Record<SyncStatus, …>` meer, en dat is
 * geen omzeiling van de compileerfout maar het antwoord erop: `Exclude`
 * houdt de dwang overeind, want komt er ooit een vijfde boekingswaarde bij,
 * dan breken ze opnieuw.
 */
import type { SyncStatus } from '@functieloonhuis/shared';

/** De toestanden die een zichtbare chip krijgen — `niet_boekend` toont niets. */
type ZichtbareSyncStatus = Exclude<SyncStatus, 'niet_boekend'>;

const SYNC_LABELS: Record<ZichtbareSyncStatus, string> = {
  pending: 'Nog te boeken',
  synced: 'Geboekt',
  failed: 'Mislukt',
};

const SYNC_STYLES: Record<ZichtbareSyncStatus, string> = {
  pending: 'bg-gray-100 text-gray-600 border-gray-300',
  synced: 'bg-[#B7D945]/15 text-[#5C7A00] border-[#B7D945]/40',
  failed: 'bg-[#D64545]/10 text-[#D64545] border-[#D64545]/30',
};

interface SyncStatusChipProps {
  syncStatus: SyncStatus;
  onRetry?: () => void;
  isRetrying?: boolean;
}

export function SyncStatusChip({ syncStatus, onRetry, isRetrying = false }: SyncStatusChipProps) {
  // Besluit B-V1: bij een soort die nooit geboekt wordt staat er niets.
  if (syncStatus === 'niet_boekend') return null;

  return (
    <span className="inline-flex items-center gap-2">
      <span
        data-testid="sync-status-chip"
        className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium border ${SYNC_STYLES[syncStatus]}`}
      >
        {SYNC_LABELS[syncStatus]}
      </span>
      {syncStatus === 'failed' && onRetry && (
        <button
          type="button"
          onClick={onRetry}
          disabled={isRetrying}
          className="text-xs text-[#54949E] hover:underline disabled:opacity-40"
        >
          Opnieuw proberen
        </button>
      )}
    </span>
  );
}
